'use client';

import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, Transition, useTime, useTransform } from 'motion/react';
import type { HTMLMotionProps } from 'motion/react';
import { Icon, IconProps } from '../../icons';
import './IconButton.css';

export type IconButtonVariant = 'primary' | 'secondary' | 'transparent';
export type IconButtonSize = 'sm' | 'md' | 'lg';
export type IconButtonState = 'idle' | 'processing' | 'success' | 'error';

export interface IconButtonProps extends Omit<HTMLMotionProps<'button'>, 'ref' | 'children'> {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  leadingVisual?: IconProps['name'] | React.ReactNode;
  trailingVisual?: IconProps['name'] | React.ReactNode;
  loading?: boolean;
  state?: IconButtonState;
  onStateChange?: (state: IconButtonState) => void;
  showStateBadge?: boolean;
  stateLabels?: {
    idle?: string;
    processing?: string;
    success?: string;
    error?: string;
  };
}

function isReactElement(node: unknown): node is React.ReactElement {
  return React.isValidElement(node);
}

const STROKE_WIDTH = 1.5;
const VIEW_BOX_SIZE = 24;

// Size mapping for consistent icon sizing across all icon types
const getIconSize = (buttonSize: IconButtonSize): number => {
  const sizeMap: Record<IconButtonSize, number> = {
    sm: 16,
    md: 18,
    lg: 20,
  };
  return sizeMap[buttonSize];
};

const getSvgProps = (size: number) => ({
  width: size,
  height: size,
  viewBox: `0 0 ${VIEW_BOX_SIZE} ${VIEW_BOX_SIZE}`,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: STROKE_WIDTH,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});

const springConfig: Transition = {
  type: 'spring',
  stiffness: 150,
  damping: 20,
};

const animations = {
  initial: { pathLength: 0 },
  animate: { pathLength: 1 },
  transition: springConfig,
};

const secondLineAnimation = {
  ...animations,
  transition: { ...springConfig, delay: 0.1 },
};

const srOnlyStyle: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

function usePrefersReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReduced(mediaQuery.matches);
    update();
    mediaQuery.addEventListener?.('change', update);
    return () => mediaQuery.removeEventListener?.('change', update);
  }, []);
  return prefersReduced;
}

function CheckIcon({ prefersReduced, size }: { prefersReduced: boolean; size: IconButtonSize }) {
  const iconSize = getIconSize(size);
  const svgProps = getSvgProps(iconSize);

  return (
    <motion.svg {...svgProps}>
      <motion.polyline
        points="4 12 9 17 20 6"
        initial={prefersReduced ? false : animations.initial}
        animate={prefersReduced ? undefined : animations.animate}
        transition={prefersReduced ? { duration: 0 } : animations.transition}
      />
    </motion.svg>
  );
}

function LoaderIcon({ prefersReduced, size }: { prefersReduced: boolean; size: IconButtonSize }) {
  const time = useTime();
  const rotate = useTransform(time, [0, 1000], [0, 360], { clamp: false });
  const iconSize = getIconSize(size);
  const svgProps = getSvgProps(iconSize);

  return (
    <motion.div
      style={{
        rotate: prefersReduced ? undefined : rotate,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: iconSize,
        height: iconSize,
        willChange: prefersReduced ? undefined : 'transform',
      }}
    >
      <motion.svg {...svgProps}>
        <motion.path
          d="M21 12a9 9 0 1 1-6.219-8.56"
          initial={prefersReduced ? false : animations.initial}
          animate={prefersReduced ? undefined : animations.animate}
          transition={prefersReduced ? { duration: 0 } : animations.transition}
        />
      </motion.svg>
    </motion.div>
  );
}

function XIcon({ prefersReduced, size }: { prefersReduced: boolean; size: IconButtonSize }) {
  const iconSize = getIconSize(size);
  const svgProps = getSvgProps(iconSize);

  return (
    <motion.svg {...svgProps}>
      <motion.line
        x1="6"
        y1="6"
        x2="18"
        y2="18"
        initial={prefersReduced ? false : animations.initial}
        animate={prefersReduced ? undefined : animations.animate}
        transition={prefersReduced ? { duration: 0 } : animations.transition}
      />
      <motion.line
        x1="18"
        y1="6"
        x2="6"
        y2="18"
        initial={prefersReduced ? false : secondLineAnimation.initial}
        animate={prefersReduced ? undefined : secondLineAnimation.animate}
        transition={prefersReduced ? { duration: 0 } : secondLineAnimation.transition}
      />
    </motion.svg>
  );
}

const StateIcon = ({
  state,
  prefersReduced,
  size,
}: {
  state: IconButtonState;
  prefersReduced: boolean;
  size: IconButtonSize;
}) => {
  let IconComponent = <></>;
  const iconSize = getIconSize(size);

  switch (state) {
    case 'idle':
      IconComponent = <></>;
      break;
    case 'processing':
      IconComponent = <LoaderIcon prefersReduced={prefersReduced} size={size} />;
      break;
    case 'success':
      IconComponent = <CheckIcon prefersReduced={prefersReduced} size={size} />;
      break;
    case 'error':
      IconComponent = <XIcon prefersReduced={prefersReduced} size={size} />;
      break;
  }

  return (
    <motion.span
      className="icon-button__state-icon-container"
      animate={{ width: state === 'idle' ? 0 : iconSize }}
      transition={{ type: 'spring', stiffness: 600, damping: 30 }}
    >
      <AnimatePresence initial={false}>
        <motion.span
          key={state}
          className="icon-button__state-icon"
          initial={{ y: -40, scale: 0.5, filter: 'blur(6px)' }}
          animate={{ y: 0, scale: 1, filter: 'blur(0px)' }}
          exit={{ y: 12, scale: 0.8, filter: 'blur(4px)', opacity: 0 }}
          transition={{ duration: 0.15, ease: 'easeInOut' }}
          style={{ willChange: 'transform, filter' }}
        >
          {IconComponent}
        </motion.span>
      </AnimatePresence>
    </motion.span>
  );
};

const IconButtonInternal = (
  {
    variant = 'primary',
    size = 'md',
    leadingVisual,
    trailingVisual,
    disabled,
    loading = false,
    state = 'idle',
    showStateBadge = false,
    stateLabels,
    ...rest
  }: IconButtonProps,
  ref: React.Ref<HTMLButtonElement>,
) => {
  const { onClick: userOnClick, className: userClassName, type: userType, ...buttonProps } = rest;
  const { onStateChange: _omitOnStateChange, ...sanitizedButtonProps } = buttonProps as {
    onStateChange?: (state: IconButtonState) => void;
  } & Record<string, unknown>;
  void _omitOnStateChange;

  const isProcessing = loading || state === 'processing';
  const isDisabled = disabled || isProcessing;
  const prefersReduced = usePrefersReducedMotion();

  // Use provided icon, preferring leadingVisual if both are set.
  const providedVisual = useMemo(
    () => leadingVisual ?? trailingVisual,
    [leadingVisual, trailingVisual],
  );

  const defaultStateLabels = {
    idle: 'Idle',
    processing: 'Processing',
    success: 'Done',
    error: 'Error',
  };

  const finalStateLabels = { ...defaultStateLabels, ...stateLabels };

  const renderVisual = (visual: IconProps['name'] | React.ReactNode) => {
    if (!visual) return null;
    if (typeof visual === 'string') {
      // Use explicit pixel values for better mobile compatibility
      const iconSizeMap: Record<IconButtonSize, number> = {
        sm: 16,
        md: 18,
        lg: 20,
      };
      const iconSize = iconSizeMap[size];
      const iconColor: IconProps['color'] | undefined = undefined;
      return (
        <Icon
          name={visual as IconProps['name']}
          size={iconSize}
          {...(iconColor ? { color: iconColor } : {})}
          className="icon-button__icon"
          aria-hidden="true"
        />
      );
    }
    if (isReactElement(visual)) {
      const visualElement = visual as React.ReactElement<{ className?: string }>;
      return React.cloneElement(visualElement, {
        className: `icon-button__icon ${visualElement.props.className || ''}`.trim(),
        'aria-hidden': true,
      } as Partial<typeof visualElement.props> & { 'aria-hidden': true });
    }
    if (process.env.NODE_ENV === 'development') {
      console.warn('Invalid visual prop provided to IconButton');
    }
    return null;
  };

  return (
    <motion.button
      ref={ref}
      type={userType ?? 'button'}
      className={`icon-button${isDisabled ? ' icon-button--disabled' : ''}${userClassName ? ` ${userClassName}` : ''}`}
      data-variant={variant}
      data-size={size}
      data-state={state}
      disabled={isDisabled}
      aria-busy={isProcessing || undefined}
      whileHover={!isDisabled && !prefersReduced ? { scale: 1.03 } : undefined}
      whileTap={!isDisabled && !prefersReduced ? { scale: 0.97 } : undefined}
      transition={{ type: 'spring', stiffness: 600, damping: 35 }}
      style={{ willChange: !prefersReduced ? 'transform' : undefined }}
      {...(sanitizedButtonProps as typeof buttonProps)}
      onClick={(e) => userOnClick?.(e)}
    >
      {showStateBadge ? (
        <>
          <StateIcon state={state} prefersReduced={prefersReduced} size={size} />
          <span aria-live="polite" style={srOnlyStyle}>
            {finalStateLabels[state]}
          </span>
        </>
      ) : isProcessing ? (
        <>
          <LoaderIcon prefersReduced={prefersReduced} size={size} />
          <span aria-live="polite" style={srOnlyStyle}>
            {finalStateLabels.processing}
          </span>
        </>
      ) : (
        renderVisual(providedVisual)
      )}
    </motion.button>
  );
};

export const IconButton = React.memo(
  forwardRef<HTMLButtonElement, IconButtonProps>(IconButtonInternal),
);
IconButton.displayName = 'IconButton';
